'use client';

import { useEffect } from 'react';

import { RotateCcw } from 'lucide-react';

/**
 * Site-wide error boundary with one-shot auto-recovery.
 *
 * The intermittent React #310 crash isn't confined to /chat (which has its
 * own boundary) — PostHog shows it on the homepage and /challenges too. Same
 * strategy as app/chat/error.tsx: first occurrence per path auto-resets
 * silently (most renders succeed on retry), a repeat shows one-tap reload.
 */
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const key = `app-error-retried:${window.location.pathname}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      reset();
    }
  }, [reset]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-[#0a0a0a] p-6">
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
          <span className="text-3xl">✨</span>
        </div>
        <h2 className="text-lg font-semibold text-white">
          Something went sideways
        </h2>
        <p className="max-w-xs text-sm text-gray-400">
          A quick reload fixes it — everything you did is saved.
        </p>
        <button
          onClick={() => {
            sessionStorage.removeItem(
              `app-error-retried:${window.location.pathname}`,
            );
            reset();
          }}
          className="from-eros-pink to-eros-purple inline-flex items-center gap-2 rounded-full bg-gradient-to-r px-6 py-2.5 font-semibold text-white transition-opacity hover:opacity-90"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Reload
        </button>
      </div>
    </div>
  );
}
